import type { LatLngPoint } from "./smoothing";

/**
 * 配送員 → 你家的提示弧線（純幾何，不依賴任何地圖套件）。
 *
 * 為什麼是弧線而不是直線：這條線的用途是「把兩個點在視覺上綁成一組」，
 * 不是告訴客戶車子會怎麼走。直線反而容易被讀成「他會直直開過來」，
 * 一條懸空拱起、明顯離開地面的弧線一看就知道是示意，不會被當成導航路線——
 * 這也正好符合這一版刻意不揭露實際路線的前提（那會帶出其他客戶的住家）。
 *
 * 實作是二次貝茲曲線。嚴格說不是拋物線的解析式，但二次貝茲畫出來就是一段拋物線弧，
 * 而且只要三個控制點、每幀重算的成本可以忽略。
 */

/**
 * 弧高相對於兩點距離的比例。
 * 0.22 在一兩公里的距離下拱得太誇張，整條線比地圖本身還搶眼；0.10 看得出是懸空的
 * 弧線，但仍然像是「把兩個點連起來」而不是一道彩虹。
 */
export const ARC_CURVATURE = 0.1;
/**
 * 取樣段數。
 * 24 段在手機上已經看不出折線，而這些點是每次重畫都要全部送進算繪器的——
 * 段數直接等於重畫成本，給得比看得出來的還多只是白花。
 */
export const ARC_SEGMENTS = 24;
/**
 * 影子的弧度相對於主弧的比例。
 *
 * 嚴格的物理投影應該是兩點之間的直線（弧是往畫面上方拱的，正上方打光的影子就是那條弦）。
 * 這裡刻意留一點弧度不畫成直線：一條從配送員筆直連到你家的線，正是我們一開始
 * 不用直線的理由——會被讀成「他會直直開過來」。壓扁到剩一成多，看起來仍然是貼地的影子，
 * 但明顯是彎的，不會被當成路線。
 */
export const ARC_SHADOW_RATIO = 0.12;

/** 影子那條弧的曲率。主弧與影子必須共用這個推導，否則兩條線會慢慢對不上。 */
export const ARC_SHADOW_CURVATURE = ARC_CURVATURE * ARC_SHADOW_RATIO;

const toRad = (d: number) => (d * Math.PI) / 180;

/**
 * 兩點之間的弧線取樣點（含頭尾，長度為 segments + 1）。
 *
 * @param curvature 弧高／距離。0 = 直線。
 */
/**
 * 弧線的貝茲控制點（回傳經緯度）。
 *
 * 抽出來是因為有兩個用途：buildArcPath 取樣成折線（給對焦範圍與測試用），
 * 以及畫面上直接畫一條 `M … Q …` 路徑（只投影三個點，見 bezierArc.ts）。
 * 兩者必須是同一條曲線，所以控制點只能有一份定義。
 */
export const arcControlPoint = (
  from: LatLngPoint,
  to: LatLngPoint,
  curvature: number = ARC_CURVATURE
): LatLngPoint => {
  const mid = { lat: (from.lat + to.lat) / 2, lng: (from.lng + to.lng) / 2 };
  /**
   * 先投影到局部平面再算垂直方向。
   *
   * 直接拿經緯度當 x/y 的話，經度 1 度在台灣的實際長度只有緯度 1 度的 0.91 倍，
   * 算出來的「垂直」在畫面上是斜的——東西向的兩點弧會偏扁、南北向的會偏鼓。
   */
  const latScale = Math.cos(toRad((from.lat + to.lat) / 2));
  if (!Number.isFinite(latScale) || Math.abs(latScale) < 1e-6) return mid;

  const dx = (to.lng - from.lng) * latScale;
  const dy = to.lat - from.lat;
  const len = Math.hypot(dx, dy);
  // 兩點重合時沒有垂直方向可言（法向量會變 0/0）
  if (len === 0) return mid;

  /**
   * 中點往「畫面上方」推，讓弧線看起來是懸空拱起來的。
   *
   * 關鍵是取法向量之後要看它的北向分量，必要時翻面。只取固定一側（例如行進方向的左側）
   * 的話，配送員在你家北邊時弧線會往下垂——那看起來是貼在地上的一條線，不是懸空的弧。
   * 地圖不會旋轉（永遠正北朝上），所以「北」就是「畫面上方」。
   *
   * 左法向量是 (-dy, dx)，它的北向分量是 dx；dx < 0（往西走）時翻面，弧才會繼續朝上。
   * dx = 0（兩點在同一條經線上、畫面上是一條垂直線）時沒有「上方」可言，
   * 此時固定往側邊拱，不會退化成直線。
   *
   * 乘 2 是因為二次貝茲在 t=0.5 只走到控制點的一半，控制點要推 2 倍弧高才會得到想要的高度。
   */
  const upward = dx >= 0 ? 1 : -1;
  const offset = len * curvature * 2 * upward;
  return {
    lat: mid.lat + (dx / len) * offset,
    lng: mid.lng + ((-dy / len) * offset) / latScale,
  };
};

/**
 * 兩點之間的弧線取樣點（含頭尾，長度為 segments + 1）。
 *
 * 畫面上的弧線不走這裡——那是 bezierArc.ts 直接畫一條貝茲路徑。這支的用途是把同一條
 * 曲線攤成點陣，給 fitBounds 算對焦範圍（弧頂會凸出兩點連線之外）與測試使用。
 *
 * @param curvature 弧高／距離。0 = 直線。
 */
export const buildArcPath = (
  from: LatLngPoint,
  to: LatLngPoint,
  curvature: number = ARC_CURVATURE,
  segments: number = ARC_SEGMENTS
): LatLngPoint[] => {
  const latScale = Math.cos(toRad((from.lat + to.lat) / 2));
  if (segments < 1 || !Number.isFinite(latScale) || Math.abs(latScale) < 1e-6) return [from, to];
  if (from.lat === to.lat && from.lng === to.lng) return [from, to];

  const ctrl = arcControlPoint(from, to, curvature);
  const ax = from.lng * latScale;
  const ay = from.lat;
  const bx = to.lng * latScale;
  const by = to.lat;
  const cx = ctrl.lng * latScale;
  const cy = ctrl.lat;

  const points: LatLngPoint[] = [from];
  for (let i = 1; i < segments; i += 1) {
    const t = i / segments;
    const u = 1 - t;
    const x = u * u * ax + 2 * u * t * cx + t * t * bx;
    const y = u * u * ay + 2 * u * t * cy + t * t * by;
    points.push({ lat: y, lng: x / latScale });
  }
  // 頭尾直接用原值，不讓 latScale 的來回換算在端點留下浮點誤差
  points.push(to);
  return points;
};
